/**
 * "When was the widget last seen?" — one answer for every screen.
 *
 * Three reads carry a timestamp for the same event: the site list
 * (`GET /sites`, the newest accepted impression per key, read once when the
 * portal loads), install health (`GET /dashboard/install-health`, the newest
 * accepted impression per website over 30 days) and the key-usage list
 * (`GET /sites/:key/origins`, `last_seen_at`, written at most every 5 minutes
 * per website). Each screen used to show its own, loaded at a different time,
 * so one site read "8 minutes ago" on Sites & keys, "5 minutes ago" under
 * "Where your key is used" and "6 minutes ago" on the Dashboard (third live
 * review, 2026-09-27).
 *
 * Now every screen asks this provider, which polls install health once a
 * minute for the whole organisation and answers with the NEWEST of what it
 * knows and what the screen's own row says. The rendering side shares one
 * clock (ui/clock.tsx), so equal timestamps always read the same.
 */
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import type { InstallHealth } from '../api/types';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import { useQuery } from '../ui/hooks';

/** The newest of some ISO timestamps (unparseable and empty ones ignored), or null. */
export function latestIso(...values: Array<string | null | undefined>): string | null {
  let best: string | null = null;
  let bestMs = -Infinity;
  for (const v of values) {
    if (!v) continue;
    const ms = Date.parse(v);
    if (Number.isNaN(ms) || ms <= bestMs) continue;
    best = v;
    bestMs = ms;
  }
  return best;
}

const norm = (origin: string) => origin.replace(/\/+$/, '').toLowerCase();

export interface LastSeen {
  /** The newest impression for a site key: its own row's value or anything install health saw. */
  site: (siteKey: string | null | undefined, own?: string | null) => string | null;
  /** The newest impression for one website under a site key. */
  origin: (siteKey: string | null | undefined, origin: string, own?: string | null) => string | null;
  /** Install health for the whole organisation, when this role may read it. */
  health: InstallHealth | null;
}

const LastSeenContext = createContext<LastSeen | null>(null);

export function LastSeenProvider({ children }: { children: ReactNode }) {
  const { api, role } = useSession();
  const enabled = can(role, 'installHealth');
  const health = useQuery(() => api.installHealth(), [], { enabled, pollMs: 60_000 });
  const data = health.data;
  const bySite = useMemo(() => {
    const out = new Map<string, string>();
    for (const o of data?.origins ?? []) {
      if (!o.site_key || !o.last_impression_at) continue;
      const best = latestIso(out.get(o.site_key), o.last_impression_at);
      if (best) out.set(o.site_key, best);
    }
    return out;
  }, [data]);
  const byOrigin = useMemo(() => {
    const out = new Map<string, string>();
    for (const o of data?.origins ?? []) {
      if (!o.last_impression_at) continue;
      out.set(`${o.site_key ?? ''} ${norm(o.origin)}`, o.last_impression_at);
    }
    return out;
  }, [data]);
  const site = useCallback((siteKey: string | null | undefined, own?: string | null) => latestIso(own, siteKey ? bySite.get(siteKey) : null), [bySite]);
  const origin = useCallback(
    (siteKey: string | null | undefined, o: string, own?: string | null) => latestIso(own, byOrigin.get(`${siteKey ?? ''} ${norm(o)}`)),
    [byOrigin],
  );
  const value = useMemo(() => ({ site, origin, health: data ?? null }), [site, origin, data]);
  return <LastSeenContext.Provider value={value}>{children}</LastSeenContext.Provider>;
}

const FALLBACK: LastSeen = { site: (_k, own) => own ?? null, origin: (_k, _o, own) => own ?? null, health: null };

export function useLastSeen(): LastSeen {
  return useContext(LastSeenContext) ?? FALLBACK;
}
